/**
 * Option Chain Builder
 * Generates strike ranges and builds OCC option symbols for option chains
 */

import { buildOccSymbol } from "../../shared/symbols/occOptionSymbol";
import { normalizeUnderlying } from "../../shared/symbols/occOptionSymbol";
import { coerceExcelDateToYyyyMmDd } from "../../shared/excel/normalize";
import { UserInputError } from "../../shared/errors/UserInputError";

export interface BuildChainInput {
  underlying: string;
  expiration: string;
  rights: ("C" | "P")[];
  strikeMin: number;
  strikeMax: number;
  strikeStep: number;
}

export interface ChainContract {
  occSymbol: string;
  underlying: string;
  expiration: string;
  strike: number;
  right: "C" | "P";
}

/**
 * Generate strike range with integer scaling to avoid float drift
 * @param min - Minimum strike (inclusive)
 * @param max - Maximum strike (inclusive)
 * @param step - Strike step size
 * @returns Array of strike values
 * @throws UserInputError if step <= 0 or min >= max
 */
export function generateStrikes(min: number, max: number, step: number): number[] {
  if (step <= 0) {
    throw new UserInputError("INVALID_STEP", "Strike step must be greater than 0", "strikeStep");
  }
  
  if (min >= max) {
    throw new UserInputError("INVALID_RANGE", "Strike minimum must be less than maximum", "strikeMin");
  }
  
  // Use integer scaling (×1000) to avoid float drift
  const minScaled = Math.round(min * 1000);
  const maxScaled = Math.round(max * 1000);
  const stepScaled = Math.round(step * 1000);
  
  const strikes: number[] = [];
  for (let scaled = minScaled; scaled <= maxScaled; scaled += stepScaled) {
    strikes.push(scaled / 1000);
  }
  
  return strikes;
}

/**
 * Build option chain from input parameters
 * @param input - Chain input parameters
 * @returns Array of chain contracts with stable ordering (ascending strike, Calls then Puts)
 * @throws UserInputError if validation fails
 */
export function buildOptionChain(input: BuildChainInput): ChainContract[] {
  // Normalize underlying (reuse existing normalizer)
  let underlyingNorm: string;
  try {
    underlyingNorm = normalizeUnderlying(input.underlying);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid underlying symbol";
    throw new UserInputError("INVALID_UNDERLYING", message, "underlying");
  }
  
  // Coerce expiration to YYYY-MM-DD
  let expirationNorm: string;
  try {
    expirationNorm = coerceExcelDateToYyyyMmDd(input.expiration);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid expiration date";
    throw new UserInputError("INVALID_EXPIRATION", message, "expiration");
  }
  
  // Validate rights array
  if (!Array.isArray(input.rights) || input.rights.length === 0) {
    throw new UserInputError("INVALID_RIGHTS", "At least one right (Call or Put) must be selected", "rights");
  }
  
  const validRights = input.rights.filter(r => r === "C" || r === "P");
  if (validRights.length === 0) {
    throw new UserInputError("INVALID_RIGHTS", "Rights must be 'C' (Call) or 'P' (Put)", "rights");
  }
  
  // Generate strikes
  const strikes = generateStrikes(input.strikeMin, input.strikeMax, input.strikeStep);
  
  // Build contracts: ascending strike, Calls then Puts
  const contracts: ChainContract[] = [];
  
  for (const strike of strikes) {
    // Add Calls first if selected
    if (validRights.includes("C")) {
      const symbolResult = buildOccSymbol({
        underlying: underlyingNorm,
        side: "C",
        expiration: expirationNorm,
        strike: strike,
      });
      contracts.push({
        occSymbol: symbolResult.unspaced,
        underlying: underlyingNorm,
        expiration: expirationNorm,
        strike: strike,
        right: "C",
      });
    }
    
    // Add Puts if selected
    if (validRights.includes("P")) {
      const symbolResult = buildOccSymbol({
        underlying: underlyingNorm,
        side: "P",
        expiration: expirationNorm,
        strike: strike,
      });
      contracts.push({
        occSymbol: symbolResult.unspaced,
        underlying: underlyingNorm,
        expiration: expirationNorm,
        strike: strike,
        right: "P",
      });
    }
  }
  
  return contracts;
}
